import { useState } from 'react';
import { api, type MeResponse } from '../api';
import { messageFor } from '../App';
import { UnverifiedNotice } from './UnverifiedNotice';

/**
 * One-time suggestions, shown once and never nagged.
 *
 * Anything worth suggesting has to be findable afterwards, so every prompt here also exists
 * as an ordinary setting. A banner is a reminder that the feature exists, not the only way
 * to reach it — otherwise dismissing it hides the feature for good, which is how good
 * features end up unused.
 */
export function Suggestions({
  me,
  onChanged,
  onGoToSettings,
}: {
  me: MeResponse;
  onChanged: () => void;
  onGoToSettings: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const dismissed = me.user?.dismissedPrompts ?? [];
  const showCalendar = !me.calendarConnected && !dismissed.includes('calendar');

  if (!showCalendar) return null;

  async function dismiss() {
    setBusy(true);
    try {
      await api.dismissPrompt('calendar');
      onChanged();
    } catch (err) {
      setError(messageFor(err));
      setBusy(false);
    }
  }

  return (
    <div className="suggestion">
      {error && <div className="notice error">{error}</div>}
      <div className="suggestion-body">
        <strong>Bring lessons in from your calendar</strong>
        <p className="muted">
          If co-ops, classes and museum trips are already on your Google Calendar, they can be
          pulled in as draft entries for you to confirm — read-only, and you choose which
          students each one counts for.
        </p>
        <UnverifiedNotice />
      </div>
      <div className="suggestion-actions">
        <button
          className="primary"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            setError(undefined);
            try {
              const { url } = await api.connectCalendar();
              window.location.assign(url);
            } catch (err) {
              setError(messageFor(err));
              setBusy(false);
            }
          }}
        >
          Connect calendar
        </button>
        <button disabled={busy} onClick={onGoToSettings}>
          Later
        </button>
        <button className="link" disabled={busy} onClick={() => void dismiss()}>
          Don’t suggest this again
        </button>
      </div>
    </div>
  );
}
